import * as React from 'react';
import * as _ from 'lodash';
import { IApplicationState, RecipientActionCreators, AppActionCreators, IPreselectListResponse, DonorPreselectActionCreators, IContactState, IPreselectLookupData } from '@nkr/store/index';
import { IBaseResponse, ICenter, IFwtSummary, IListItem, IMortalityCalculations, IPreselectDonorInfo, IPreselectResult, IPreselectStatistics, IRecipient } from '@nkr/models/index';
import { DonorPreselectBulkUpdateType, DonorPreselectReviewStatus, MatchOfferDeclineReason, MortalityMatchType } from '@nkr/enums/index';
import { Dispatch, connect } from 'react-redux';
import { Alert } from 'react-bootstrap';
import { Actions } from '../potential-donors/models/actions';
import { toast } from 'react-toastify';
import { IRowData } from './models/row-data';
import List3 from './list3';
import HardBlock from '@nkr/views/match/modals/hard-block';
import ConfirmNotFullyConfident from '../potential-donors/confirm-not-fully-confident';
import ConfirmBulkDecline from '../potential-donors/confirm-bulk-decline';
import ConfirmBulkUpdate from '../potential-donors/confirm-bulk-update';
import FwtDetailsModal from '@nkr/views/target-new/modals/fwt-details-modal';
import MortalityBreakdown from '@nkr/components/mortality-breakdown-modal';
import BulkActions from './bulk-actions';

interface IProps {
    centerId: number
}

interface IStateProps {
    contact: IContactState,
    centerList: ICenter[],
    groupIsSuper: boolean
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getPreselects: (centerId: number) => Promise<IPreselectListResponse>,
    getPreselectLookupData: () => Promise<IPreselectLookupData>,
    acceptDonor: (recipientId: number, donorId: number, isFullyConfident: boolean, hasMortalityRisk: boolean) => Promise<IBaseResponse>,
    declineDonor: (recipientId: number, recipientCenterId: number, donorId: number, declineReasons: { reasonId: number, otherText: string }[], isGlobalDecline: boolean, hasMortalityRisk: boolean) => Promise<IBaseResponse>,
    updateReviewStatus: (recipientId: number, donorId: number, reviewStatus: DonorPreselectReviewStatus) => Promise<IBaseResponse>,
    bulkUpdatePreselects: (bulkUpdateType: DonorPreselectBulkUpdateType, recipientId: number, list: number[], isAccepted: boolean, isFullyConfident: boolean, hardBlockReason: number, reviewStatus: DonorPreselectReviewStatus) => Promise<IBaseResponse>,
    bulkDeclineMultiPreselects: (recipientId: number, list: number[], hardBlockReasons: { reasonId: number, otherText: string }[]) => Promise<IBaseResponse>
}

type TProps = IStateProps & IDispatchProps & IProps;

const otherTypes = [MatchOfferDeclineReason.DonorOther, MatchOfferDeclineReason.SurgeryDateOther, MatchOfferDeclineReason.PatientDeactivateOther] as number[];

interface IState {
    list: IRowData[],
    hardBlockReasonList: IListItem[],
    hardblockReasons: number[],
    donorPreselectReviewStatusList: IListItem[],
    notFullyConfident: {
        show: boolean,
        recipientId: number,
        donorId: number
    },
    hardBlock: {
        show: boolean,
        recipientId: number,
        donor: IPreselectDonorInfo,
        isBulkUpdate: boolean,
        reasons: number[],
        otherText: string
    },
    bulkDecline: {
        show: boolean,
        reasons: string[],
        otherText: string
    },
    bulkUpdate: {
        action: string,
        reason: number
    },
    sortByAccessor: string,
    sortByDesc: boolean,
    selectAll: boolean,
    recipientHasMortaliyRisk: boolean,
    riskPercent: number,
    fetching: boolean,
    showBusy: boolean,
    mortalityBreakdown: {
        show: boolean,
        recipientAlias: string,
        data: IMortalityCalculations
    },
    fwtSummary: IFwtSummary,
    fwt: {
        show: boolean
    },
    statusFilter: number,
    showConfirmBulkUpdate: boolean,
    loading: boolean
}

class DonorPreselect extends React.Component<TProps, IState> {
    readonly state: IState = {
        list: [],
        hardBlockReasonList: [],
        hardblockReasons: [],
        donorPreselectReviewStatusList: [],
        notFullyConfident: {
            show: false,
            recipientId: 0,
            donorId: 0
        },
        hardBlock: {
            show: false,
            recipientId: 0,
            donor: null,
            isBulkUpdate: false,
            reasons: [],
            otherText: null
        },
        bulkDecline: {
            show: false,
            reasons: [],
            otherText: null
        },
        bulkUpdate: {
            action: '',
            reason: 0
        },
        sortByAccessor: '',
        sortByDesc: false,
        selectAll: false,
        recipientHasMortaliyRisk: false,
        riskPercent: 0,
        fetching: true,
        showBusy: false,
        mortalityBreakdown: {
            show: false,
            recipientAlias: '',
            data: null
        },
        fwtSummary: null,
        fwt: {
            show: false,
        },
        statusFilter: -1,
        showConfirmBulkUpdate: false,
        loading: true
    }

    componentDidMount = () => {
        this.getData('');
    }

    getData = async (message: string) => {
        await this.props.showLoading('Getting preselects, one moment please...');

        Promise.all([
            this.props.getPreselects(this.props.centerId),
            this.props.getPreselectLookupData()
        ])
            .then(([response, lookupDataResponse]) => {
                if (response.data.success) {
                    let preselects = response.data.results;
                    let { hardBlockReasonList, donorPreselectReviewStatusList } = lookupDataResponse.data.results;

                    let list = this.setupList(preselects);

                    this.setState({
                        list,
                        hardBlockReasonList,
                        donorPreselectReviewStatusList,
                        fetching: false,
                        recipientHasMortaliyRisk: false,
                        riskPercent: 0,
                        loading: false
                    }, () => {
                        if (message) {
                            toast.success(message);
                        }
                    });
                }
            })
            .finally(() => {
                this.props.hideLoading();
            });
    }

    refreshData = (message: string) => {
        this.props.getPreselects(this.props.centerId)
            .then(response => {
                if (response.data.success) {
                    let preselects = response.data.results;

                    let list = this.setupList(preselects);

                    this.setState({
                        list,
                        fetching: false,
                        recipientHasMortaliyRisk: false,
                        riskPercent: 0,
                        loading: false
                    }, () => {
                        if (message) {
                            toast.success(message);
                        }
                    });
                }
            })
            .finally(() => {
                this.props.hideLoading();
            });
    }

    setupList = (preselects: IPreselectResult[]) => {
        let list: IRowData[] = [];

        preselects.forEach(item => {
            let isFirst = true;

            item.donors.forEach(preselectDonor => {
                let r: IRowData = {
                    recipient: item.recipient,
                    donor: preselectDonor.donor,
                    failedAvoids: preselectDonor.failedAvoids,
                    hlaPointsA: preselectDonor.hlaPointsA,
                    hlaPointsB: preselectDonor.hlaPointsB,
                    hlaPointsDr: preselectDonor.hlaPointsDr,
                    totalHlaPoints: preselectDonor.totalHlaPoints,
                    isHardBlocked: preselectDonor.isHardBlocked,
                    singleEpletMmRisk: preselectDonor.singleEpletMmRisk,
                    lkdpiInfo: preselectDonor.lkdpiInfo,
                    categories: preselectDonor.categories,
                    reviewStatus: preselectDonor.reviewStatus,
                    donorNote: preselectDonor.donorNote,
                    matchNote: preselectDonor.matchNote,
                    bmtMm: preselectDonor.bmtMm,
                    isPairedDonor: preselectDonor.isPairedDonor,
                    matchOfferId: preselectDonor.matchOfferId,
                    responseDeadLineDate: preselectDonor.responseDeadLineDate,
                    isReviewRequested: preselectDonor.isReviewRequested,
                    reviewRequestedDate: preselectDonor.reviewRequestedDate,
                    isSelected: false,
                    isFirst,
                };

                list.push(r);

                isFirst = false;
            });
        });

        return list;
    }

    acceptDonor = (recipientId: number, donorId: number, isFullyConfident: boolean, hasMoralityRisk: boolean = false) => {
        this.props.showLoading('Accepting donor, one moment please...').then(() => {
            this.props.acceptDonor(recipientId, donorId, isFullyConfident, hasMoralityRisk)
                .then(response => {
                    if (response.data.success) {
                        this.refreshData('Donor preselect updated successfully');
                    }
                })
                .catch((err: any) => {
                    this.props.hideLoading();
                });
        });
    }

    declineDonor = (recipientId: number, donorId: number, hardBlockReasons: number[], hardBlockComments: string, isGlobalDecline: boolean) => {
        let { recipientHasMortaliyRisk } = this.state;

        let declineReasons = hardBlockReasons.map(x => {
            return { reasonId: x, otherText: hardBlockComments && otherTypes.includes(x) ? hardBlockComments : null }
        })

        this.props.showLoading('Declining donor, one moment please...').then(() => {
            this.props.declineDonor(recipientId, this.props.centerId, donorId, declineReasons, isGlobalDecline, recipientHasMortaliyRisk)
                .then(response => {
                    if (response.data.success) {
                        this.handleCancelHardBlock();
                        this.refreshData('Donor preselect updated successfully');
                    }
                })
                .catch((err: any) => {
                    this.props.hideLoading()
                });
        })
    }

    handleAction = (recipientId: number, donorId: number, action: Actions) => {
        let recp = this.state.list.find(x => x.recipient.id == recipientId)?.recipient;
        let recipientHasMortaliyRisk = recp?.hasMortalityRisk ?? false;

        if (action == Actions.NotAcceptable) {
            let item = this.state.list.find(x => x.donor.id == donorId);

            this.setState({
                hardBlock: {
                    show: true,
                    recipientId,
                    donor: item.donor,
                    isBulkUpdate: false,
                    reasons: [],
                    otherText: null
                },
                recipientHasMortaliyRisk,
                riskPercent: recp?.mortalityRiskPercent ?? 0
            });

            return;
        }

        if (action == Actions.Evaluate) {
            window.open('/center/' + this.props.centerId + '/recipient/' + recipientId + '/evaluate-donor/' + donorId, '_blank');
            return;
        }

        if (action == Actions.AcceptableFullyConfident) {
            this.acceptDonor(recipientId, donorId, true, recipientHasMortaliyRisk);
        }

        if (action == Actions.AcceptableNotFullyConfident) {
            if (recp?.cpra4 >= 99) {
                this.setState({
                    notFullyConfident: { show: true, recipientId, donorId }
                });
            } else {
                this.acceptDonor(recipientId, donorId, false, recipientHasMortaliyRisk);
            }
        }
    }

    handleHardBlock = (ids: number[], otherText: string) => {
        let { hardBlock } = this.state;

        if (hardBlock.isBulkUpdate) {
            this.setState({
                hardBlock: {
                    show: false,
                    donor: null,
                    recipientId: 0,
                    reasons: ids,
                    otherText,
                    isBulkUpdate: true
                }
            }, () => {
                this.handleShowConfirmBulkDecline()
            })
        } else {
            this.declineDonor(hardBlock.recipientId, hardBlock.donor.id, ids, otherText, false);
        }
    }

    handleHideConfirmBulkDecline = () => {
        this.setState({
            hardBlock: {
                show: false,
                donor: null,
                recipientId: 0,
                reasons: [],
                isBulkUpdate: false,
                otherText: null
            },
            bulkDecline: {
                show: false,
                reasons: [],
                otherText: null
            },
            hardblockReasons: []
        });
    }

    handleConfirmBulkDecline = () => {
        let { hardBlock, bulkDecline } = this.state;
        this.handleHardBlockDonors(hardBlock.reasons, bulkDecline.otherText);
    }

    handleHardBlockDonors = (ids: number[], otherText: string) => {
        let hardblockReasons = ids.map(x => ({
            reasonId: x,
            otherText: otherTypes.includes(x) ? otherText : null
        }));

        let recipientGroups = _.uniq(this.state.list.filter(x => x.isSelected).map(x => x.recipient.id));

        this.props.showLoading('Updating selected donors, one moment please...').then(() => {
            let updates = recipientGroups.map(recipientId => {
                let list = this.state.list
                    .filter(x => x.isSelected && x.recipient.id == recipientId)
                    .map(x => x.donor.id);

                return this.props.bulkDeclineMultiPreselects(recipientId, list, hardblockReasons);
            });

            Promise.all(updates)
                .then(responses => {
                    let allSuccess = responses.every(r => r.data.success);

                    if (allSuccess) {
                        this.refreshData('Donor preselect(s) Updated');
                        this.handleCancelHardBlock();
                    }
                })
                .catch((err: any) => {
                    this.props.hideLoading();
                });
        });
    }

    handleCancelHardBlock = () => {
        this.setState({
            hardBlock: {
                show: false,
                donor: null,
                recipientId: 0,
                isBulkUpdate: false,
                reasons: [],
                otherText: null
            },
            bulkDecline: {
                show: false,
                reasons: [],
                otherText: null
            },
            hardblockReasons: []
        });
    }

    handleConfirmNotFullyConfidentYes = () => {
        let { recipientId, donorId } = this.state.notFullyConfident;

        this.setState({
            notFullyConfident: {
                show: false,
                recipientId: 0,
                donorId: 0
            }
        }, () => {
            this.acceptDonor(recipientId, donorId, false);
        });
    }

    handleConfirmNotFullyConfidentNo = () => {
        this.setState({
            notFullyConfident: {
                show: false,
                recipientId: 0,
                donorId: 0
            }
        });
    }

    handleReviewStatusChange = (recipientId: number, donorId: number, reviewStatus: DonorPreselectReviewStatus) => {
        let list = _.cloneDeep(this.state.list);

        this.setState({
            showBusy: true
        }, () => {
            this.props.updateReviewStatus(recipientId, donorId, reviewStatus)
                .then(response => {
                    if (response.data.success) {
                        list = list.map(item => {
                            if (item.recipient.id == recipientId && item.donor.id == donorId) {
                                item.reviewStatus = reviewStatus;
                            }

                            return item;
                        });

                        this.setState({
                            list,
                            showBusy: false
                        });
                    }
                })
                .catch(error => {
                    console.log(error);
                });
        });
    }

    handleSortChange = (sortByAccessor: string, sortByDesc: boolean) => {
        this.setState({
            sortByAccessor,
            sortByDesc
        });
    }

    handleOnMultiHardblock = (isBulkUpdate: boolean = false) => {
        this.setState({
            hardBlock: {
                show: true,
                donor: null,
                recipientId: 0,
                isBulkUpdate,
                otherText: null,
                reasons: []
            }
        })
    }

    handleDonorSelectAll = (recipientId: number) => {
        let potentialDonors = _.cloneDeep(this.state.list);

        let recipientDonors = potentialDonors.filter(x => x.recipient.id == recipientId);
        let allSelected = recipientDonors.every(x => x.isSelected);

        potentialDonors = potentialDonors.map(x => {
            if (x.recipient.id == recipientId) {
                x.isSelected = !allSelected;
            }

            return x;
        });

        this.setState({
            list: potentialDonors,
            selectAll: !allSelected
        });
    }

    handleDonorSelected = (recipientId: number, donorId: number, checked: boolean) => {
        let potentialDonors = _.cloneDeep(this.state.list);

        potentialDonors = potentialDonors.map(x => {
            if (x.donor.id == donorId && x.recipient.id == recipientId) {
                x.isSelected = checked;
            }

            return x;
        });

        this.setState({
            list: potentialDonors
        });
    }

    handleBulkUpdate = (recipientId: number, bulkUpdateAction: string, isAccepted: boolean, isFullyConfident: boolean, hardBlockReason: number, reviewStatus: DonorPreselectReviewStatus) => {
        let list = this.state.list.filter(x => x.isSelected).map(x => x.donor.id);
        var bulkUpdateType = bulkUpdateAction == 'decline' ? DonorPreselectBulkUpdateType.Decision : DonorPreselectBulkUpdateType.Review;

        this.props.showLoading('Updating selected donors, one moment please...').then(() => {
            this.props.bulkUpdatePreselects(bulkUpdateType, recipientId, list, isAccepted, isFullyConfident, hardBlockReason, reviewStatus)
                .then(response => {
                    if (response.data.success) {
                        this.refreshData('Donor preselects bulk updated successfully');
                    }
                })
                .catch((err: any) => {
                    this.props.hideLoading();
                });
        });
    }

    handleShowMortalityBreakdown = (recipient: IRecipient) => {
        this.setState({
            mortalityBreakdown: {
                show: true,
                recipientAlias: recipient.alias,
                data: recipient.mortalityCalculations
            }
        });
    }

    handleMortalityBreakdownClose = () => {
        this.setState({
            mortalityBreakdown: {
                show: false,
                recipientAlias: '',
                data: null
            }
        });
    }

    handleShowFwt = (recipient: IRecipient) => {
        this.setState({
            fwt: { show: true },
            fwtSummary: recipient.fwtSummaryData
        });
    }

    handleFwtClose = () => {
        this.setState({
            fwt: { show: false }
        });
    }

    handleShowConfirmBulkDecline = () => {
        let { hardBlock } = this.state;

        let currentHardBlockReasons = hardBlock.reasons;

        let filteredReasons = this.state.hardBlockReasonList.filter(x => currentHardBlockReasons.includes(x.id))

        let reasons = filteredReasons.map(x => x.description);

        this.setState({
            bulkDecline: {
                show: true,
                reasons,
                otherText: hardBlock.otherText
            }
        })
    }

    handleBulkUpdateActionChange = (action: string) => {
        this.setState({
            bulkUpdate: {
                ...this.state.bulkUpdate,
                action,
                reason: action == 'decline' ? 1 : this.state.bulkUpdate.reason
            }
        });
    }

    handleBulkUpdateReasonChange = (reason: number) => {
        this.setState({
            bulkUpdate: {
                ...this.state.bulkUpdate,
                reason
            }
        });
    }

    handleStatusFilterChange = (value: number) => {
        this.setState({ statusFilter: value });
    }

    handleConfirmBulkUpdate = () => {
        if (this.state.bulkUpdate.action === 'decline') {
            this.handleOnMultiHardblock(true);
        } else {
            this.setState({ showConfirmBulkUpdate: true });
        }
    }

    handleConfirmBulkUpdateYes = () => {
        let { action, reason } = this.state.bulkUpdate;

        let isAccepted: boolean = null;
        let isFullyConfident: boolean = null;

        if (action != 'review') {
            isAccepted = action == 'accepted';
            isFullyConfident = action === 'decline' ? null : action == 'accepted-fully-confident';
        }

        let recipientGroups = _.uniq(this.state.list.filter(x => x.isSelected).map(x => x.recipient.id));
        let bulkUpdateType = action == 'decline' ? DonorPreselectBulkUpdateType.Decision : DonorPreselectBulkUpdateType.Review;

        this.setState({
            showConfirmBulkUpdate: false
        }, () => {
            this.props.showLoading('Updating selected donors, one moment please...').then(() => {
                let updates = recipientGroups.map(recipientId => {
                    let list = this.state.list
                        .filter(x => x.isSelected && x.recipient.id == recipientId)
                        .map(x => x.donor.id);

                    return this.props.bulkUpdatePreselects(bulkUpdateType, recipientId, list, isAccepted, isFullyConfident, 0, reason as DonorPreselectReviewStatus);
                });

                Promise.all(updates)
                    .then(responses => {
                        let allSuccess = responses.every(r => r.data.success);

                        if (allSuccess) {
                            this.refreshData('Donor preselects bulk updated successfully');
                        }
                    });
            }).catch((err: any) => {
                this.props.hideLoading();
            });
        });
    }

    handleConfirmBulkUpdateNo = () => {
        this.setState({ showConfirmBulkUpdate: false });
    }

    getBulkUpdateReason = (): string => {
        let { action, reason } = this.state.bulkUpdate;

        if (action === 'review') {
            let found = this.state.donorPreselectReviewStatusList.find(x => x.id == reason);
            return found != null ? found.description : 'Remove Assigned Status';
        }

        return '';
    }

    getDisableBulkUpdateButton = (): boolean => {
        let { action, reason } = this.state.bulkUpdate;
        let hasSelected = this.state.list.filter(x => x.isSelected).length > 0;

        if ((action === 'review' || action === 'decline') && hasSelected && reason !== -1) {
            return false;
        }

        return true;
    }

    render = () => {
        const recipientList = _.uniqBy(this.state.list.map(x => x.recipient), x => x.id);

        const masterList = recipientList.map(recipient => {
            return {
                recipient,
                donors: this.state.list.filter(x => x.recipient.id == recipient.id)
            };
        });

        const hardBlockRecipient = recipientList.find(x => x.id == this.state.hardBlock.recipientId);
        const warnOnHighDeclineRatio = hardBlockRecipient != null ? hardBlockRecipient.warnOnHighDeclineRatio : false;
        const hardBlockReasonChoices = this.state.hardBlockReasonList.filter(x => this.state.hardblockReasons.includes(x.id)).map(x => x.description);

        return (
            <div style={{ padding: '0px 10px', minHeight: '75vh' }}>
                {this.state.loading == false && (
                    <>
                        <BulkActions
                            statusFilter={this.state.statusFilter}
                            bulkUpdate={this.state.bulkUpdate}
                            disableBulkUpdate={this.getDisableBulkUpdateButton()}
                            donorPreselectReviewStatusList={this.state.donorPreselectReviewStatusList}
                            onStatusFilterChange={this.handleStatusFilterChange}
                            onBulkUpdateActionChange={this.handleBulkUpdateActionChange}
                            onBulkUpdateReasonChange={this.handleBulkUpdateReasonChange}
                            onConfirmBulkUpdate={this.handleConfirmBulkUpdate}
                        />

                        {masterList.map((item, index) =>
                            <List3
                                key={'list-of-donors-' + index}
                                recipient={item.recipient}
                                list={item.donors}
                                recipientCount={masterList.length}
                                hardBlockReasonList={this.state.hardBlockReasonList}
                                hardBlockReasonChoices={hardBlockReasonChoices}
                                donorPreselectReviewStatusList={this.state.donorPreselectReviewStatusList}
                                statusFilter={this.state.statusFilter}
                                groupIsSuper={this.props.groupIsSuper}
                                fetching={this.state.fetching}
                                handleAction={this.handleAction}
                                handleReviewStatusChange={this.handleReviewStatusChange}
                                handleSortChange={this.handleSortChange}
                                handleMultiHardblock={this.handleOnMultiHardblock}
                                handleSelectAll={this.handleDonorSelectAll}
                                handleSelected={this.handleDonorSelected}
                                handleBulkUpdate={this.handleBulkUpdate}
                                handleShowMortalityBreakdown={this.handleShowMortalityBreakdown}
                                handleShowFwt={this.handleShowFwt}
                            />
                        )}

                        {masterList.length == 0 ?
                            <div style={{ textAlign: 'center' }}>
                                <div className='bg-warning' style={{ padding: '1px 10px', borderRadius: 4 }}><h4>There are no donors pending pre-selection for any recipients</h4></div>
                            </div>
                            :
                            <div style={{ paddingLeft: 5 }}>{this.state.list.filter(x => !x.isPairedDonor).length} pending preselects for {masterList.length} patients ...</div>
                        }
                    </>
                )}

                {this.state.loading && (
                    <Alert bsSize='sm'>Searching for pending preselects, one moment please...</Alert>
                )}

                <HardBlock
                    show={this.state.hardBlock.show}
                    alias={this.state.hardBlock.donor != null ? this.state.hardBlock.donor.alias : ''}
                    centerAlias={this.state.hardBlock.donor != null ? this.state.hardBlock.donor.centerAlias : ''}
                    isGlobalDecline={false}
                    warnOnHighDeclineRatio={warnOnHighDeclineRatio}
                    hasMortalityRisk={this.state.recipientHasMortaliyRisk}
                    mortalityRiskPercent={this.state.riskPercent}
                    hardBlockReasons={this.state.hardBlockReasonList}
                    onDeclineDonor={this.handleHardBlock}
                    onCancel={this.handleCancelHardBlock}
                />

                <ConfirmBulkDecline
                    show={this.state.bulkDecline.show}
                    reasons={this.state.bulkDecline.reasons}
                    list={this.state.list.filter(x => x.isSelected).map(x => x)}
                    isPreselectPage={true}
                    onYes={this.handleConfirmBulkDecline}
                    onNo={this.handleHideConfirmBulkDecline}
                />

                <ConfirmNotFullyConfident
                    show={this.state.notFullyConfident.show}
                    onYes={this.handleConfirmNotFullyConfidentYes}
                    onNo={this.handleConfirmNotFullyConfidentNo}
                />

                <ConfirmBulkUpdate
                    show={this.state.showConfirmBulkUpdate}
                    action={this.state.bulkUpdate.action}
                    reason={this.getBulkUpdateReason()}
                    list={this.state.list.filter(x => x.isSelected).map(x => x)}
                    isPreselectPage={true}
                    onYes={this.handleConfirmBulkUpdateYes}
                    onNo={this.handleConfirmBulkUpdateNo}
                />

                <MortalityBreakdown
                    show={this.state.mortalityBreakdown.show}
                    recipientAlias={this.state.mortalityBreakdown.recipientAlias}
                    data={this.state.mortalityBreakdown.data}
                    onClose={this.handleMortalityBreakdownClose}
                />

                <FwtDetailsModal
                    show={this.state.fwt.show}
                    summaryData={this.state.fwtSummary}
                    onClose={this.handleFwtClose}
                />
            </div>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        contact: appState.contact,
        centerList: appState.centers.list,
        groupIsSuper: appState.app.user.group.isSuper
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getPreselects: (centerId) => {
            return dispatch(RecipientActionCreators.getPreselects(centerId));
        },
        getPreselectLookupData: () => {
            return dispatch(RecipientActionCreators.getPreselectLookupData());
        },
        acceptDonor: (recipientId, donorId, isFullyConfident, hasMortalityRisk) => {
            return dispatch(DonorPreselectActionCreators.acceptDonor(recipientId, donorId, isFullyConfident, hasMortalityRisk, MortalityMatchType.Preselects));
        },
        declineDonor: (recipientId, recipientCenterId, donorId, declineReasons, isGlobalDecline, hasMortalityRisk) => {
            return dispatch(DonorPreselectActionCreators.declineDonor(recipientId, recipientCenterId, donorId, 'Pre-Select', declineReasons, isGlobalDecline, hasMortalityRisk, MortalityMatchType.Preselects));
        },
        updateReviewStatus: (recipientId, donorId, reviewStatus) => {
            return dispatch(DonorPreselectActionCreators.updateReviewStatus(recipientId, donorId, reviewStatus));
        },
        bulkUpdatePreselects: (bulkUpdateType, recipientId, list, isAccepted, isFullyConfident, hardBlockReason, reviewStatus) => {
            return dispatch(DonorPreselectActionCreators.bulkUpdatePreselects(bulkUpdateType, recipientId, list, isAccepted, isFullyConfident, hardBlockReason, reviewStatus));
        },
        bulkDeclineMultiPreselects: (recipientId, list, hardBlockReasons) => {
            return dispatch(DonorPreselectActionCreators.bulkDeclineMultiPreselects(recipientId, list, hardBlockReasons))
        }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps, mapDispatchToProps)(DonorPreselect)